/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    UpcomingOffersApi,
    UpcomingOffersGetV2Request,
} from 'client-code/upcoming-offers';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IUpcomingOffersServiceRepository } from '../interfaces';
import { Log } from '../logging';
import { TServicesParams, TUpcomingOffersList, TUpcomingOffersV2Params } from '../types';
import { UniqueIdentifier } from '../utils';

export class UpcomingOffersServiceRepositoryImplV2 implements IUpcomingOffersServiceRepository {
    private static TAG: string = 'UpcomingOffersServiceRepositoryImplV2';

    private mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private async getUpcomingOffersFromServiceV2(
        upcomingOffersParams: TUpcomingOffersV2Params,
    ): Promise<TUpcomingOffersList> {
        try {
            const { objectId, offset, limit, minEndTime, requestToken, screenType } =
                upcomingOffersParams;
            const commonHeaders = new CommonHeaders(screenType).headers;

            const configurationParameters: ConfigurationParameters = {
                apiKey: this.mParams.accessToken,
                basePath: this.mParams.basePath,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            const requestParameters: UpcomingOffersGetV2Request = {
                objectId,
                offset,
                limit,
                minEndTime,
                requestToken,
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationVersion: commonHeaders.ApplicationVersion,
                bodyId: this.mParams.bodyId,
                applicationName: commonHeaders.ApplicationName,
                deviceType: commonHeaders.DeviceType,
                productName: commonHeaders.ProductName,
                xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
                whiny: this.mParams.whiny || 'false',
            };

            const configuration = new Configuration(configurationParameters);
            const upcomingOffersApi = new UpcomingOffersApi(configuration);
            const upcomingOffersApiResponse =
                await upcomingOffersApi.upcomingOffersGetV2(requestParameters);

            const totalCount = parseInt(upcomingOffersApiResponse.headers['x-total-count'], 10);

            return { ...upcomingOffersApiResponse.data, totalCount };
        } catch (error: any) {
            Log.e(
                UpcomingOffersServiceRepositoryImplV2.TAG,
                'getUpcomingOffersFromServiceV2()',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchUpcomingOffers(
        upcomingOffersParams: TUpcomingOffersV2Params,
    ): Promise<TUpcomingOffersList> {
        try {
            return await this.getUpcomingOffersFromServiceV2(upcomingOffersParams);
        } catch (error) {
            Log.e(UpcomingOffersServiceRepositoryImplV2.TAG, 'error', error);
            throw error;
        }
    }
}
